import {memo, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'
import {useFrame} from '@react-three/fiber'
import * as THREE from 'three'
import type {CustomDecal} from '../../types/customDecalTypes'
import {customDecalPlaneSize} from '../../types/customDecalTypes'
import {DISPLAY_EXTRUDE} from '../workerGeo/helpers'
import {getPreviewCustomDecalSnapshot, subscribePreviewCustomDecal} from './previewCustomDecal'

interface CustomDecalMeshV2Props {
  decal: CustomDecal
  deviceWidth: number
  deviceHeight: number
  deviceDepth: number
}

interface LoadedTexture {
  url: string
  texture: THREE.Texture
}

// Tiny gap so the decal sits just proud of the surface (avoids z-fighting) while
// staying visually flush — matches the camera-lens / side-button offset, not a
// floating overlay. The side faces sit exactly at ±width/2 / ±height/2 (see
// buildSideButtons) and the front glass top at depth/2 + DISPLAY_EXTRUDE.
const SURFACE_GAP = 0.05

/**
 * A user image rendered as a flat textured plane glued to the device surface.
 * Reuses the 3D-sprite material model (unlit → MeshBasicMaterial, else PBR +
 * shadows) but derives its transform from the device dimensions + placement so
 * it conforms to the front face / a side edge and rides the device transform.
 */
const CustomDecalMeshV2 = memo(({decal, deviceWidth, deviceHeight, deviceDepth}: CustomDecalMeshV2Props) => {
  const [loaded, setLoaded] = useState<LoadedTexture | null>(null)
  const meshRef = useRef<THREE.Mesh>(null)

  // Live preview during drag — overrides the committed Redux transform so the
  // plane tracks the cursor smoothly without per-tick dispatches.
  const previewMap = useSyncExternalStore(subscribePreviewCustomDecal, getPreviewCustomDecalSnapshot)
  const preview = previewMap?.[decal.id] ?? null
  const offsetU = preview?.offsetU ?? decal.offsetU
  const offsetV = preview?.offsetV ?? decal.offsetV
  const size = preview?.size ?? decal.size
  const rotation = preview?.rotation ?? decal.rotation

  // Tie both shadow fields to `!unlit` — VSM needs receiver depth, so a lit decal
  // must both cast and receive (mirrors Sprite3DMeshV2).
  const lit = !decal.unlit
  useLayoutEffect(() => {
    const mesh = meshRef.current
    if (!mesh) return
    mesh.castShadow = lit
    mesh.receiveShadow = lit
  }, [lit, loaded])

  useFrame(() => {
    const mesh = meshRef.current
    if (!mesh) return
    mesh.castShadow = lit
    mesh.receiveShadow = lit
  })

  useEffect(() => {
    const url = decal.imageUrl
    if (!url) return
    const loader = new THREE.TextureLoader()
    loader.setCrossOrigin('anonymous')
    let disposed = false
    let tex: THREE.Texture | null = null
    loader.load(
      url,
      (texture) => {
        if (disposed) {
          texture.dispose()
          return
        }
        texture.colorSpace = THREE.SRGBColorSpace
        texture.anisotropy = 8
        tex = texture
        setLoaded({url, texture})
      },
      undefined,
      () => {
        // Swallow load errors — decal simply doesn't render until the URL resolves.
      },
    )
    return () => {
      disposed = true
      if (tex) tex.dispose()
    }
  }, [decal.imageUrl])

  const [planeWidth, planeHeight] = useMemo(() => customDecalPlaneSize({...decal, size}), [decal, size])

  // Surface transform: group position + base orientation per placement.
  // `customDecalPlaneSize` returns [U, V] where U is the plane's local X (width)
  // and V its local Y (height); the base Euler orients local +Z to the outward normal.
  const {position, baseRotation} = useMemo((): {
    position: [number, number, number]
    baseRotation: [number, number, number]
  } => {
    const frontZ = deviceDepth / 2 + DISPLAY_EXTRUDE + SURFACE_GAP
    const halfW = deviceWidth / 2 + SURFACE_GAP
    const halfH = deviceHeight / 2 + SURFACE_GAP
    const halfQ = Math.PI / 2
    switch (decal.placement) {
      case 'right':
        // normal +X; U = depth (Z), V = height (Y)
        return {position: [halfW, offsetV, offsetU], baseRotation: [0, halfQ, 0]}
      case 'left':
        // normal -X
        return {position: [-halfW, offsetV, offsetU], baseRotation: [0, -halfQ, 0]}
      case 'top':
        // normal +Y; U = width (X), V = depth (Z)
        return {position: [offsetU, halfH, offsetV], baseRotation: [-halfQ, 0, 0]}
      case 'bottom':
        // normal -Y
        return {position: [offsetU, -halfH, offsetV], baseRotation: [halfQ, 0, 0]}
      case 'front':
      default:
        // normal +Z; U = X, V = Y
        return {position: [offsetU, offsetV, frontZ], baseRotation: [0, 0, 0]}
    }
  }, [decal.placement, offsetU, offsetV, deviceWidth, deviceHeight, deviceDepth])

  const decalRotZ = useMemo(() => THREE.MathUtils.degToRad(rotation), [rotation])

  // Only render once the loaded texture matches the current decal URL.
  if (!loaded || loaded.url !== decal.imageUrl) return null

  return (
    <group position={position} rotation={baseRotation}>
      {/* Inner mesh rotates about its own normal (local +Z) for the in-plane rotation.
          `key` forces a fresh mesh when unlit toggles so shadow flags take at construction. */}
      <mesh key={decal.unlit ? 'unlit' : 'lit'} ref={meshRef} rotation={[0, 0, decalRotZ]} castShadow={lit}>
        <planeGeometry args={[planeWidth, planeHeight]} />
        {decal.unlit ? (
          <meshBasicMaterial map={loaded.texture} transparent alphaTest={0.01} side={THREE.DoubleSide} />
        ) : (
          <meshPhysicalMaterial
            map={loaded.texture}
            transparent
            alphaTest={0.01}
            side={THREE.DoubleSide}
            metalness={decal.material.metalness}
            roughness={decal.material.roughness}
            clearcoat={decal.material.clearcoat}
            clearcoatRoughness={decal.material.clearcoatRoughness}
            reflectivity={decal.material.reflectivity}
          />
        )}
      </mesh>
    </group>
  )
})
CustomDecalMeshV2.displayName = 'CustomDecalMeshV2'

export default CustomDecalMeshV2
